import {
  IDLE_INSTALL_PROMPT,
  INSTALL_PROMPT_DAY_MS,
  INSTALL_PROMPT_STORAGE_KEY,
  createInstallPromptSnapshot,
  isIosInstallEnvironment,
  parseInstallPromptCooldown,
  serializeInstallPromptCooldown,
  type InstallPromptNavigator,
  type InstallPromptSnapshot,
} from "./install-prompt-model.ts";

interface InstallPromptChoice {
  readonly outcome: "accepted" | "dismissed";
}

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<InstallPromptChoice>;
  prompt(): Promise<void>;
}

export interface InstallPromptEventTarget {
  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): void;
  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
  ): void;
}

export interface InstallPromptDisplayMode {
  readonly matches: boolean;
  addEventListener(
    type: "change",
    listener: EventListenerOrEventListenerObject,
  ): void;
  removeEventListener(
    type: "change",
    listener: EventListenerOrEventListenerObject,
  ): void;
}

export interface InstallPromptStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

export interface InstallPromptManagerOptions {
  readonly eventTarget?: InstallPromptEventTarget | null;
  readonly displayMode?: InstallPromptDisplayMode | null;
  readonly navigatorState?: InstallPromptNavigator | null;
  readonly storage?: InstallPromptStorage | null;
  readonly now?: () => number;
}

export type InstallPromptRequestResult =
  | "accepted"
  | "dismissed"
  | "installed"
  | "failed"
  | "unavailable";

export interface InstallPromptManager {
  readonly getSnapshot: () => InstallPromptSnapshot;
  subscribe(listener: () => void): () => void;
  start(): InstallPromptSnapshot;
  recordMeaningfulEngagement(): void;
  requestInstall(): Promise<InstallPromptRequestResult>;
  dismiss(): void;
  stop(): void;
}

function resolveEventTarget(): InstallPromptEventTarget | null {
  try {
    return typeof window === "undefined" ? null : window;
  } catch {
    return null;
  }
}

function resolveDisplayMode(): InstallPromptDisplayMode | null {
  try {
    return typeof window === "undefined"
      ? null
      : window.matchMedia("(display-mode: standalone)");
  } catch {
    return null;
  }
}

function resolveNavigatorState(): InstallPromptNavigator | null {
  try {
    return typeof navigator === "undefined"
      ? null
      : (navigator as Navigator & { readonly standalone?: boolean });
  } catch {
    return null;
  }
}

function resolveStorage(): InstallPromptStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isBeforeInstallPromptEvent(
  event: Event,
): event is BeforeInstallPromptEvent {
  try {
    const candidate = event as Partial<BeforeInstallPromptEvent>;
    return (
      typeof candidate.prompt === "function" &&
      !!candidate.userChoice &&
      typeof candidate.userChoice.then === "function"
    );
  } catch {
    return false;
  }
}

function snapshotsMatch(
  first: InstallPromptSnapshot,
  second: InstallPromptSnapshot,
): boolean {
  return (
    first.status === second.status &&
    first.mode === second.mode &&
    first.action === second.action &&
    first.code === second.code
  );
}

export function createInstallPromptManager(
  options: InstallPromptManagerOptions = {},
): InstallPromptManager {
  const listeners = new Set<() => void>();
  const now = options.now ?? Date.now;

  let snapshot = IDLE_INSTALL_PROMPT;
  let eventTarget: InstallPromptEventTarget | null = null;
  let displayMode: InstallPromptDisplayMode | null = null;
  let navigatorState: InstallPromptNavigator | null = null;
  let storage: InstallPromptStorage | null = null;
  let deferredEvent: BeforeInstallPromptEvent | null = null;
  let cooldownUntilDay: number | null = null;
  let requestPromise: Promise<InstallPromptRequestResult> | null = null;
  let activeRequestToken: symbol | null = null;
  let engaged = false;
  let installed = false;
  let pageSuppressed = false;
  let started = false;
  let lifecycleGeneration = 0;

  const setSnapshot = (next: InstallPromptSnapshot) => {
    if (snapshotsMatch(snapshot, next)) {
      return;
    }

    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  };

  const readNow = (): number => {
    try {
      const value = now();
      return Number.isFinite(value) ? value : 0;
    } catch {
      return 0;
    }
  };

  const removeCooldown = () => {
    cooldownUntilDay = null;
    try {
      storage?.removeItem(INSTALL_PROMPT_STORAGE_KEY);
    } catch {
      // Promotion preferences must never affect portal availability.
    }
  };

  const hasActiveCooldown = (): boolean => {
    if (pageSuppressed) {
      return true;
    }
    if (cooldownUntilDay === null) {
      return false;
    }
    if (cooldownUntilDay > Math.floor(readNow() / INSTALL_PROMPT_DAY_MS)) {
      return true;
    }

    removeCooldown();
    return false;
  };

  const isInstalledDisplay = (): boolean => {
    try {
      return (
        displayMode?.matches === true || navigatorState?.standalone === true
      );
    } catch {
      return true;
    }
  };

  const publishInstalled = () => {
    installed = true;
    pageSuppressed = true;
    deferredEvent = null;
    removeCooldown();
    setSnapshot(createInstallPromptSnapshot("installed"));
  };

  const publishEligibility = () => {
    if (installed || isInstalledDisplay()) {
      publishInstalled();
      return;
    }
    if (hasActiveCooldown()) {
      deferredEvent = null;
      setSnapshot(createInstallPromptSnapshot("deferred"));
      return;
    }
    if (!engaged) {
      setSnapshot(IDLE_INSTALL_PROMPT);
      return;
    }
    if (deferredEvent) {
      setSnapshot(
        createInstallPromptSnapshot("eligible", {
          mode: "browser",
          action: "install",
        }),
      );
      return;
    }
    if (isIosInstallEnvironment(navigatorState)) {
      setSnapshot(
        createInstallPromptSnapshot("manual", {
          mode: "ios",
          action: "instructions",
        }),
      );
      return;
    }

    setSnapshot(IDLE_INSTALL_PROMPT);
  };

  const storeCooldown = () => {
    pageSuppressed = true;
    const currentTime = readNow();
    try {
      const value = serializeInstallPromptCooldown(currentTime);
      storage?.setItem(INSTALL_PROMPT_STORAGE_KEY, value);
      cooldownUntilDay = parseInstallPromptCooldown(value, currentTime);
    } catch {
      cooldownUntilDay = null;
    }
  };

  const handleBeforeInstallPrompt: EventListener = (event) => {
    if (!isBeforeInstallPromptEvent(event)) {
      return;
    }

    event.preventDefault();
    if (
      requestPromise ||
      snapshot.status === "prompting" ||
      installed ||
      isInstalledDisplay() ||
      hasActiveCooldown()
    ) {
      deferredEvent = null;
      return;
    }

    deferredEvent = event;
    publishEligibility();
  };

  const handleAppInstalled: EventListener = () => {
    publishInstalled();
  };

  const handleDisplayModeChange: EventListener = () => {
    if (isInstalledDisplay()) {
      publishInstalled();
    }
  };

  const handleStorage: EventListener = (event) => {
    const storageEvent = event as Partial<StorageEvent>;
    if (storageEvent.key !== INSTALL_PROMPT_STORAGE_KEY) {
      return;
    }
    if (storageEvent.newValue === null) {
      cooldownUntilDay = null;
      return;
    }
    if (typeof storageEvent.newValue !== "string") {
      return;
    }

    const nextEligibleDay = parseInstallPromptCooldown(
      storageEvent.newValue,
      readNow(),
    );
    if (nextEligibleDay === null) {
      return;
    }

    cooldownUntilDay = nextEligibleDay;
    deferredEvent = null;
    if (!installed) {
      setSnapshot(createInstallPromptSnapshot("deferred"));
    }
  };

  const start = (): InstallPromptSnapshot => {
    if (started) {
      return snapshot;
    }

    started = true;
    lifecycleGeneration += 1;
    eventTarget =
      "eventTarget" in options
        ? (options.eventTarget ?? null)
        : resolveEventTarget();
    displayMode =
      "displayMode" in options
        ? (options.displayMode ?? null)
        : resolveDisplayMode();
    navigatorState =
      "navigatorState" in options
        ? (options.navigatorState ?? null)
        : resolveNavigatorState();
    storage =
      "storage" in options ? (options.storage ?? null) : resolveStorage();

    let storedValue: string | null = null;
    try {
      storedValue = storage?.getItem(INSTALL_PROMPT_STORAGE_KEY) ?? null;
    } catch {
      storedValue = null;
    }
    cooldownUntilDay = parseInstallPromptCooldown(storedValue, readNow());
    if (storedValue !== null && cooldownUntilDay === null) {
      removeCooldown();
    }

    eventTarget?.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt,
    );
    eventTarget?.addEventListener("appinstalled", handleAppInstalled);
    eventTarget?.addEventListener("storage", handleStorage);
    displayMode?.addEventListener("change", handleDisplayModeChange);
    publishEligibility();
    return snapshot;
  };

  const requestInstall = (): Promise<InstallPromptRequestResult> => {
    if (requestPromise) {
      return requestPromise;
    }
    if (
      !started ||
      snapshot.status !== "eligible" ||
      !deferredEvent ||
      installed ||
      hasActiveCooldown()
    ) {
      return Promise.resolve("unavailable");
    }

    const promptEvent = deferredEvent;
    deferredEvent = null;
    const requestGeneration = lifecycleGeneration;
    setSnapshot(
      createInstallPromptSnapshot("prompting", {
        mode: "browser",
        action: "install",
      }),
    );

    let promptResult: Promise<void>;
    try {
      promptResult = Promise.resolve(promptEvent.prompt());
    } catch {
      setSnapshot(
        createInstallPromptSnapshot("error", {
          mode: "browser",
          action: "dismiss",
          code: "PROMPT_FAILED",
        }),
      );
      return Promise.resolve("failed");
    }

    const requestToken = Symbol("install-prompt-request");
    activeRequestToken = requestToken;
    const currentRequest = (async () => {
      try {
        await promptResult;
        const choice = await promptEvent.userChoice;
        if (!started || requestGeneration !== lifecycleGeneration) {
          return "unavailable";
        }
        if (installed || isInstalledDisplay()) {
          publishInstalled();
          return "installed";
        }
        if (choice.outcome === "accepted") {
          pageSuppressed = true;
          setSnapshot(createInstallPromptSnapshot("accepted"));
          return "accepted";
        }
        if (choice.outcome === "dismissed") {
          storeCooldown();
          setSnapshot(createInstallPromptSnapshot("deferred"));
          return "dismissed";
        }
        throw new Error("Unexpected install choice.");
      } catch {
        if (!started || requestGeneration !== lifecycleGeneration) {
          return "unavailable";
        }
        setSnapshot(
          createInstallPromptSnapshot("error", {
            mode: "browser",
            action: "dismiss",
            code: "PROMPT_FAILED",
          }),
        );
        return "failed";
      } finally {
        if (activeRequestToken === requestToken) {
          activeRequestToken = null;
          requestPromise = null;
        }
      }
    })();

    requestPromise = currentRequest;
    return currentRequest;
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start,
    recordMeaningfulEngagement() {
      if (installed || engaged) {
        return;
      }
      engaged = true;
      publishEligibility();
    },
    requestInstall,
    dismiss() {
      if (!["eligible", "manual", "error"].includes(snapshot.status)) {
        return;
      }
      deferredEvent = null;
      storeCooldown();
      setSnapshot(createInstallPromptSnapshot("deferred"));
    },
    stop() {
      if (!started) {
        return;
      }
      started = false;
      lifecycleGeneration += 1;
      eventTarget?.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      eventTarget?.removeEventListener("appinstalled", handleAppInstalled);
      eventTarget?.removeEventListener("storage", handleStorage);
      displayMode?.removeEventListener("change", handleDisplayModeChange);
      eventTarget = null;
      displayMode = null;
      navigatorState = null;
      storage = null;
      deferredEvent = null;
      activeRequestToken = null;
      requestPromise = null;
      listeners.clear();
    },
  };
}

export const portalInstallPrompt = createInstallPromptManager();

export function recordPortalInstallEngagement(): void {
  portalInstallPrompt.recordMeaningfulEngagement();
}
